import React, { useState, useRef, useEffect, memo, useCallback } from "react";
import {
  // تم إضافة memo و useCallback
  Bell,
  User,
  LogOut,
  KeyRound,
  ChevronDown,
  Menu,
  X,
  Eye,
  EyeOff,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";

// المكون الفرعي للـ Floating Label (تم نقله خارج المكون Header لضمان عدم فقدان الـ Focus)
const FloatingInput = memo(
  ({ label, id, value, onChange, show, setShow, Icon }) => (
    <div className="relative group w-full">
      <input
        type={show ? "text" : "password"}
        id={id}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder=" "
        className="block w-full p-4 pr-12 pl-12 text-right bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-[#263238] outline-none transition-all peer"
      />
      <label
        htmlFor={id}
        className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-4 z-10 origin-[right] right-12 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-4 peer-focus:text-[#263238] pointer-events-none"
      >
        {label}
      </label>
      {Icon && (
        <Icon
          className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-400"
          size={20}
        />
      )}
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute top-1/2 left-4 -translate-y-1/2 text-gray-400 hover:text-[#263238] transition-colors z-20"
      >
        {show ? <EyeOff size={20} /> : <Eye size={20} />}
      </button>
    </div>
  ),
);

// تسمية المكون لسهولة التتبع في React DevTools
FloatingInput.displayName = "FloatingInput";

const Header = ({ isSidebarOpen, setSidebarOpen, title = "لوحة التحكم" }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

  // States للموديل والباسورد
  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const [loading, setLoading] = useState(false);
  const [passData, setPassData] = useState({ old: "", new: "", confirm: "" });

  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // قراءة المستخدم "داخل" المكون لضمان ظهوره في كل الصفحات
  const user = JSON.parse(localStorage.getItem("user")) || {
    firstName: "Ahmed",
    lastName: "Ezz",
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // 1. التحقق من إدخال جميع الحقول
    if (!passData.old || !passData.new || !passData.confirm) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }

    // 2. التحقق من تطابق كلمة المرور الجديدة
    if (passData.new !== passData.confirm) {
      toast.error("كلمة المرور الجديدة وتأكيدها غير متطابقين");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // التعديل الجوهري هنا: تغيير .post إلى .put لتوافق الـ Route في الباك إند
      // والتأكد من إرسال الحقول بالأسماء المتوقعة: oldPassword و newPassword
      await axios.put(
        "/api/auth/change-password",
        {
          oldPassword: passData.old,
          newPassword: passData.new,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      toast.success("تم تحديث كلمة المرور بنجاح");
      setIsChangePasswordOpen(false);
      setPassData({ old: "", new: "", confirm: "" }); // تصفير الحقول
    } catch (error) {
      console.error("Change Password Error:", error);
      // جلب رسالة الخطأ الصريحة من الباك إند (مثل: "كلمة المرور القديمة غير صحيحة")
      const message =
        error.response?.data?.message || "حدث خطأ أثناء تحديث كلمة المرور";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // استخدام useCallback لضمان ثبات دوال onChange
  const handleOldPassChange = useCallback((val) => {
    setPassData((prevPassData) => ({ ...prevPassData, old: val }));
  }, []);

  const handleNewPassChange = useCallback((val) => {
    setPassData((prevPassData) => ({ ...prevPassData, new: val }));
  }, []);

  const handleConfirmPassChange = useCallback((val) => {
    setPassData((prevPassData) => ({ ...prevPassData, confirm: val }));
  }, []);

  return (
    <>
      <header className="flex justify-between items-center mb-8 gap-4 px-2">
        {/* اليمين: Menu + Title */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => setSidebarOpen(!isSidebarOpen)}
            className="p-2.5 bg-white shadow-sm border border-gray-100 rounded-2xl text-gray-600 hover:bg-gray-50 transition-all active:scale-90"
          >
            <Menu size={22} />
          </button>
          <h1 className="text-[#263238] font-ibm font-bold text-lg md:text-xl truncate max-w-[120px] md:max-w-none">
            {title}
          </h1>
        </div>

        {/* الشمال: Notifications + User */}
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="bg-white p-2.5 rounded-full shadow-sm relative cursor-pointer border border-gray-100 text-gray-500 hover:bg-gray-50 transition">
            <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            <Bell size={20} />
          </div>

          <div className="relative" ref={dropdownRef}>
            <div
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-2 bg-white p-1.5 pl-3 rounded-full shadow-sm cursor-pointer border border-gray-100 active:scale-95 transition hover:bg-gray-50"
            >
              <div className="bg-[#546E7A] p-2 rounded-full text-white shrink-0">
                <User size={18} />
              </div>
              <span className="hidden md:inline text-sm font-bold text-[#263238] font-ibm mr-1">
                {user?.firstName} {user?.lastName}
              </span>
              <ChevronDown
                size={14}
                className={`text-gray-400 transition-transform ${isProfileOpen ? "rotate-180" : ""}`}
              />
            </div>

            {/* Dropdown Menu - مظبوطة ناحية اليمين */}
            {isProfileOpen && (
              <div className="absolute font-ibm left-0 mt-3 w-56 bg-white rounded-[25px] shadow-2xl border border-gray-50 py-3 z-[100] animate-in fade-in zoom-in duration-200">
                <button
                  onClick={() => {
                    setIsChangePasswordOpen(true);
                    setIsProfileOpen(false);
                  }}
                  className="w-full flex flex-row-reverse items-center justify-end gap-3 px-5 py-3 text-sm text-gray-600 hover:bg-gray-50 transition text-right group"
                >
                  
                  <span className="group-hover:text-[#546E7A]">
                    تغيير كلمة المرور
                  </span>
                  <KeyRound
                    size={17}
                    className="text-gray-400 group-hover:text-[#546E7A]"
                  />
                </button>

                <div className="h-px bg-gray-100 my-1 mx-5"></div>

                <button
                  onClick={handleLogout}
                  className="w-full flex flex-row-reverse items-center justify-end gap-3 px-5 py-3 text-sm text-red-500 hover:bg-red-50 transition text-right"
                >
                   <span className="font-bold">تسجيل الخروج</span>
                  <LogOut size={17} className="text-red-400" />
                 
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* الموديل بنفس تصميم "إضافة مشكلة جديدة" */}
      {isChangePasswordOpen && (
        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <div
              className="fixed inset-0 bg-[#263238]/60 backdrop-blur-sm animate-in fade-in duration-300"
              onClick={() => setIsChangePasswordOpen(false)}
            ></div>
            <div
              className="relative bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden text-right animate-in zoom-in-95 duration-300"
              dir="rtl"
            >
              <button
                onClick={() => setIsChangePasswordOpen(false)}
                className="absolute top-6 left-6 p-2 hover:bg-gray-100 rounded-full transition-all text-gray-400 z-50"
              >
                <X size={24} />
              </button>

              <div className="p-8 pb-10">
                <h2 className="text-2xl font-bold text-[#263238] font-ibm mb-2">
                  تغيير كلمة المرور
                </h2>
                <p className="text-gray-500 text-sm mb-10">
                  قم بتحديث بيانات الأمان الخاصة بحسابك
                </p>

                <form
                  onSubmit={handleChangePassword}
                  className="space-y-5 font-ibm"
                >
                  <FloatingInput
                    id="old"
                    label="كلمة المرور القديمة"
                    value={passData.old}
                    onChange={handleOldPassChange}
                    show={showOldPass}
                    setShow={setShowOldPass}
                    Icon={Lock}
                  />
                  <FloatingInput
                    id="new"
                    label="كلمة المرور الجديدة"
                    value={passData.new}
                    onChange={handleNewPassChange}
                    show={showNewPass}
                    setShow={setShowNewPass}
                    Icon={Lock}
                  />
                  <FloatingInput
                    id="conf"
                    label="تأكيد كلمة المرور"
                    value={passData.confirm}
                    onChange={handleConfirmPassChange}
                    show={showConfirmPass}
                    setShow={setShowConfirmPass}
                    Icon={Lock}
                  />

                  <div className="flex flex-row-reverse gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsChangePasswordOpen(false)}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold py-4 rounded-xl transition-all active:scale-[0.98]"
                    >
                      إلغاء
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`flex-[2] ${loading ? "bg-gray-400" : "bg-[#546E7A] hover:bg-[#263238]"} text-white font-bold py-4 rounded-xl transition-all active:scale-[0.98] shadow-lg shadow-[#546e7a20]`}
                    >
                      {loading ? "جاري الحفظ..." : "حفظ التغييرات"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
